import { useEffect } from "react";
import {
  AppBar,
  Footer,
  Page,
} from "../../components";
import {
  Box,
  Container,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Link,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { brandGradient } from "theme";

export const FAQPage = () => {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  return (
    <Page>
      <AppBar />

      <Box
        sx={{
          background: brandGradient,
          color: "common.white",
          textAlign: "center",
          pt: { xs: 12, sm: 13 },
        }}
      >

      {/* HERO */}
      <Box
        sx={{
          background: brandGradient,
          color: "common.white",
          py: { xs: 6, md: 10 },
          textAlign: "center",
        }}
      >
        <Typography variant="h3" fontWeight={600} mb={2}>
          Frequently Asked Questions
        </Typography>
        <Typography variant="h6" sx={{ opacity: 0.9 }}>
          Everything you need to know about Hoppenhagen & Kangoo Jumps
        </Typography>
      </Box>

      {/* FAQ CONTENT */}
      <Container maxWidth="md" sx={{ py: { xs: 6, md: 10 }, textAlign: "left" }}>
        {/* VAT QUESTION */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography fontWeight={600}>
              Why do I have to pay a more expensive membership / VAT if I’m above
              30 years old?
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography>
            From 2026, Danish law requires all fitness classes for participants 
            aged 30 and above to include 25% VAT. Classes for those under 30 remain VAT-exempt. 
            This is a legal requirement, not a decision by Hoppenhagen. 
            The Danish government is currently discussing possible VAT deductions for these services, 
            so stay updated as you may be able to deduct the VAT in the future. 

            <Link href="https://skat.dk/erhverv/moms/andre-momsemner/moms-paa-motions-og-musikundervisning-tankesport-og-andre-aktiviteter-1" target="_blank" rel="noopener">
              Read more about VAT on fitness services on the official SKAT website (in Danish)
            </Link>
            </Typography>
          </AccordionDetails>
        </Accordion>

        {/* KANGOO JUMPS */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography fontWeight={600}>
              What is Kangoo Jumps?
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography>
              Kangoo Jumps is a fun, high-energy workout performed with special
              rebound shoes designed to absorb impact and protect your joints.
              It combines cardio and coordination in one
              dynamic class, making it both effective and joyful.
            </Typography>
          </AccordionDetails>
        </Accordion>

        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography fontWeight={600}>
              Do I need jumping shoes to join a Kangoo Jumps class?
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography>
              Nope, we got you covered! Kangoo Jumps boots are provided by us.
              When you first book a class, you will be asked to provide your shoe size 
              so we can have the right boots ready for you.
            </Typography>
          </AccordionDetails>
        </Accordion>


        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography fontWeight={600}>
              What shoe sizes are available?
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography>
              Sizes start from EU 36 up to EU 44. The boots sizes come in size ranges:
              <ul>
                <li>Extra small: EU 36</li>
                <li>Small: EU 37-39</li>
                <li>Medium: EU 39-42</li>
                <li>Large: EU 42-44</li>
              </ul>
            </Typography>
          </AccordionDetails>
        </Accordion>

        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography fontWeight={600}>
              Do I need experience to join a Kangoo Jumps class?
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography>
              No experience is required. Our classes are suitable for beginners
              and advanced participants alike. Our instructors guide you
              step-by-step, and you’re always welcome to go at your own pace.
            </Typography>
          </AccordionDetails>
        </Accordion>

        {/* STUDIO */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography fontWeight={600}>
              What should I bring to class?
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography>
              Please bring comfortable workout clothes, a water bottle, and long socks, 
              as it will be more comfortable while wearing the jumping shoes.
              Kangoo Jumps boots are provided during class
              unless otherwise stated.
            </Typography>
          </AccordionDetails>
        </Accordion>

        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography fontWeight={600}>
              Can I try a class before committing to a membership?
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography>
              Yes! We offer a trial class so you can experience Hoppenhagen and
              Kangoo Jumps before choosing a membership.
            </Typography>
          </AccordionDetails>
        </Accordion>

        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography fontWeight={600}>
              Is Kangoo Jumps safe for my knees and joints?
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography>
              Yes. Kangoo Jumps boots are designed to reduce impact by up to 80%
              compared to traditional workouts. Many people choose Kangoo Jumps
              specifically because it is gentler on joints while still providing
              an intense workout.
            </Typography>
          </AccordionDetails>
        </Accordion>

        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography fontWeight={600}>
              What makes Hoppenhagen different from other studios?
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography>
              Hoppenhagen is about movement, community, and joy. Our classes are
              welcoming, inclusive, and designed to make you feel energized, 
              not intimidated. We focus on fun, music, and connection as much as
              fitness.
            </Typography>
          </AccordionDetails>
        </Accordion>

        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography fontWeight={600}>
              Can pregnant women try Kangoo Jumps?
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography>
              No, Kangoo Jumps is not recommended for pregnant women as it puts stress on
              the body that may not be safe during pregnancy. We recommend consulting with 
              your general practitioner for suitable exercise options during pregnancy.
            </Typography>
          </AccordionDetails>
        </Accordion>

      </Container>

      <Footer /></Box>
    </Page>
  );
}
